import type React from "react";
import { Anchor, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { mappingApi } from "@services/mappingApi";

interface SourcePdfLinkProps {
  jobId: string;
  fileIndex: number | null | undefined;
  filename: string | null | undefined;
  hasInputPdf?: boolean;
}

/** Resolve the signed URL on each click, never during rendering or in persisted state. */
const SourcePdfLink: React.FC<SourcePdfLinkProps> = ({
  jobId,
  fileIndex,
  filename,
  hasInputPdf = true,
}) => {
  const { t } = useTranslation();
  const name = filename?.trim();
  if (fileIndex == null) return name ? <Text size="sm">{name}</Text> : null;
  const label = name || t("jobResultsPage.tmTable.sourcePdf");

  const openPdf = async (): Promise<void> => {
    // Open synchronously from the click so browsers do not block the tab after
    // the authenticated request. Drop window.opener before navigating to S3.
    const tab = window.open("", "_blank");
    if (!tab) {
      notifications.show({
        color: "red",
        title: t("jobResultsPage.sourcePdf.failedTitle"),
        message: t("jobResultsPage.sourcePdf.popupBlocked"),
      });
      return;
    }
    tab.opener = null;
    try {
      const { download_url } = await mappingApi.getJobOriginalFile(jobId, fileIndex);
      tab.location.replace(download_url);
    } catch {
      tab.close();
      notifications.show({
        color: "red",
        title: t("jobResultsPage.sourcePdf.failedTitle"),
        message: t("jobResultsPage.sourcePdf.failed"),
      });
    }
  };

  return hasInputPdf ? (
    <Anchor
      component="button"
      type="button"
      size="sm"
      onClick={() => void openPdf()}
      aria-label={t("jobResultsPage.sourcePdf.open", { filename: label })}
      style={{ overflowWrap: "anywhere", textAlign: "left" }}
    >
      {label} <IconExternalLink size={12} aria-hidden="true" />
    </Anchor>
  ) : (
    <Text size="sm">{label}</Text>
  );
};

export default SourcePdfLink;
